import { CartUpdateEvent } from '@theme/events';
import { debounce } from '@theme/utilities';

/**
 * Keeps the theme's cart UI in step with the apps that change the cart behind its back.
 *
 * This store's cart drawer is not the theme's — the theme runs with `cart_type: page` — but
 * Planet Discounts & Upsells' Smart Cart. It owns adding and removing from its own drawer and
 * talks to Shopify's Ajax API directly, so the theme never hears about it: remove the last
 * item in the drawer and the header bubble happily keeps showing 1 until the next page load.
 *
 * Planet does announce every change on `window`, so those announcements are translated here
 * into the theme's own cart event, which is what cart-icon and cart-items-component listen to.
 *
 * Note this deliberately dispatches CartUpdateEvent and never CartAddEvent: the latter shares
 * the same event name but is what makes a drawer auto-open, and a removal must not pop a
 * drawer open. Opening is left to whoever performed the action.
 */

/** Events Planet's Smart Cart fires on `window` after it has changed the cart. */
const PLANET_CART_EVENTS = ['planet:smart-cart:add', 'planet:smart-cart:change'];

/** Quantity steppers fire in bursts, and one re-read at the end is enough. */
const SYNC_DEBOUNCE = 150;

/**
 * Re-reads the cart and tells the theme about it.
 *
 * @param {string} source - Who changed the cart, for anything listening that wants to know.
 * @returns {Promise<object | null>} The cart, or null if it could not be read.
 */
export async function syncThemeCart(source) {
  try {
    const cartUrl = window.Theme?.routes?.cart_url ?? '/cart';
    const cart = await (await fetch(`${cartUrl}.js`)).json();

    document.dispatchEvent(
      new CartUpdateEvent(cart, source, {
        source,
        itemCount: cart.item_count,
      })
    );

    return cart;
  } catch (error) {
    console.error(`Could not sync the cart after a change from ${source}:`, error);
    return null;
  }
}

const syncFromPlanet = debounce(() => syncThemeCart('planet-smart-cart'), SYNC_DEBOUNCE);

for (const eventName of PLANET_CART_EVENTS) {
  window.addEventListener(eventName, syncFromPlanet);
}
